// @givmo/board — the pause after a sign-in method is removed (CBG-FIX127; H1-A, the backend's
// `board_signing_window.py` row 4b).
//
// For 24 hours after a passkey or authenticator is removed from the account, it can't sign Board acts
// or open closed-session material; reading and replying go on. `/me` serves when that ends
// (`signing.cool_off_ends_at`, only while it is ahead) whatever else keeps the window shut, because a
// director who hasn't confirmed reads "not confirmed" first. While it runs:
//
//   * AN ACT'S ROW (S1) says so beside Sign, and pressing Sign says it in place of the sheet: no
//     confirmation opens, since confirming can't open signing. An account with no method left still
//     gets the setup offer from Sign; only the confirmation is skipped.
//   * CLOSED-SESSION MATERIAL (S2) says when it opens, with no sheet and no button.
//   * A REMOVAL BOARD CAN'T READ (S3, `factor_removal_unknown`, seconds during a deploy) serves no
//     end, so the pages keep today's lines.
//
// The time and the date are the reader's own zone's, an end with seconds shown as the next whole minute
// so the line never names a minute already begun. The end itself is checked on the reader's clock, so a
// page left open stops saying it at that instant and asks `/me` again (the signing chip's rule).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// Both, S1 and S2 as recommended in the copy round for CBG-AI5-PROPOSE-COPY1 and CBG-FIX127 (in-chat
// 2026-10-19 23:33 PDT, "looks good"; `scratchpad/2026-10-19-proposals-copy-round/copy-review.html`).
// They reuse approved words: "Signing waits until …" and "an officer can record your signature for
// you meanwhile" (B2d-lost's), and "This stays closed until …" (the read rows'; `signing-copy.ts`).

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { displayClockTime } from '../lib/display';
import { useMinuteClock } from '../lib/minute-clock';
import { boardDirectorMeKey, useDirectorMe } from './director-me';
import type { DirectorSigning } from './director-wire';

/** The pause's end while it is still ahead of `nowMs`, or null: none served, unreadable, or past. */
export function pauseEndsAt(signing: DirectorSigning | undefined, nowMs: number): string | null {
  const endsAt = signing?.cool_off_ends_at ?? null;
  if (endsAt === null) return null;
  const endsMs = Date.parse(endsAt);
  return Number.isNaN(endsMs) || endsMs <= nowMs ? null : endsAt;
}

/**
 * "3:12 PM on October 20", in the reader's own zone. An end with seconds reads as the next whole
 * minute, so the line never names a minute that has begun while signing still waits, and the date is
 * the rounded instant's: an end at 11:59:30 PM reads 12:00 AM on the next day.
 */
function timeOnDate(iso: string): string {
  const endsMs = Date.parse(iso);
  const shown = Number.isNaN(endsMs)
    ? iso
    : new Date(Math.ceil(endsMs / 60_000) * 60_000).toISOString();
  const date = new Date(shown).toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
  return `${displayClockTime(shown)} on ${date}`;
}

export const signingPauseWords = {
  /** S1: an act's row, beside Sign and in place of the sheet. */
  acts: (endsAt: string) =>
    `A passkey or authenticator was removed from your account, so signing waits until ${timeOnDate(endsAt)}. An officer can record your signature for you meanwhile.`,
  /** S2: closed-session material, with no sheet and no button. */
  reads: (endsAt: string) =>
    `A passkey or authenticator was removed from your account, so this stays closed until ${timeOnDate(endsAt)}.`,
} as const;

/**
 * The pause's end while it runs, or null. Re-read on the reader's clock at each minute and at the end
 * itself, when `/me` is asked again so the server's answer replaces the clock's.
 */
export function useSigningPause(): string | null {
  const meQuery = useDirectorMe();
  const queryClient = useQueryClient();
  const signing = meQuery.data?.signing;
  const served = signing?.cool_off_ends_at ?? null;
  const now = useMinuteClock(served);
  const endsAt = pauseEndsAt(signing, now);
  const lapsed = served !== null && endsAt === null;
  useEffect(() => {
    if (lapsed) void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
  }, [lapsed, queryClient]);
  return endsAt;
}
